import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useUser } from '../context/UserContext';
import { useDarkMode } from '../context/DarkModeContext';
import { 
  Crown, 
  Trophy, 
  Zap, 
  Menu, 
  X, 
  User,
  Home,
  Mic,
  BookOpen,
  BarChart3,
  MessageSquare,
  MessageCircle,
  LogOut,
  Settings,
  ChevronDown,
  LogIn,
  UserPlus,
  Sun,
  Moon,
  Users
} from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const profileDropdownRef = useRef(null);
  
  const { user, isAuthenticated, logout, loading } = useUser();
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  // Debug: Log user data changes
  useEffect(() => {
    console.log('🔄 Navbar user data changed:', {
      xp: user?.xp,
      crowns: user?.crowns,
      trophies: user?.trophies,
      level: user?.level
    });
    // Force re-render when user data changes
    setRenderKey(prev => prev + 1);
  }, [user]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Hide navbar on onboarding page - moved after all hooks
  if (location.pathname === '/onboarding') {
    return null;
  }

  // Debug: Log every render
  console.log('🎨 Navbar rendering with user:', user?.xp, 'renderKey:', renderKey);

  // Force re-render when user data changes
  const userDataKey = `${user?.xp}-${user?.crowns}-${user?.trophies}-${user?.level}`;

  const navigation = [
    { name: 'Home', href: '/', icon: Home, public: true },
    { name: 'Debate', href: '/live-debate', icon: Mic, public: false },
    { name: 'Training', href: '/training', icon: BookOpen, public: false },
    { name: 'Friends', href: '/friends', icon: Users, public: false },
    { name: 'Chat', href: '/chat', icon: MessageCircle, public: false },
    { name: 'Leaderboard', href: '/leaderboard', icon: BarChart3, public: true },
    { name: 'Tournament', href: '/tournament', icon: Trophy, public: false },
    { name: 'Feedback', href: '/feedback', icon: MessageSquare, public: true },
  ];

  // Filter navigation based on authentication status
  const filteredNavigation = isAuthenticated 
    ? navigation 
    : navigation.filter(item => item.public);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    const confirmed = window.confirm('Are you sure you want to sign out?');
    if (confirmed) {
      logout();
      setIsProfileDropdownOpen(false);
      navigate('/');
    }
  };

  // Get user initials for profile image
  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    } else if (user?.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  if (loading && localStorage.getItem('token')) {
    return (
      <nav className="fixed top-0 left-0 right-0 w-full bg-white dark:bg-gray-900 shadow-lg z-50 border-b border-gray-200 dark:border-gray-700 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 min-h-[4rem]">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900 dark:text-white">DebateRoyale</span>
            </div>
            <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-8 w-32 rounded"></div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="fixed top-0 left-0 right-0 w-full bg-white dark:bg-gray-900 shadow-lg z-50 border-b border-gray-200 dark:border-gray-700 safe-top">
      <div className="max-w-none mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16 min-h-[4rem]">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center"
            >
              <Crown className="w-5 h-5 text-white" />
            </motion.div>
            <span className="text-lg font-bold text-gray-900 dark:text-white hidden sm:block">DebateRoyale</span>
            <span className="text-base font-bold text-gray-900 dark:text-white sm:hidden">DR</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-5 ml-6">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                    isActive(item.href)
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* User Section */}
          <div className="hidden lg:flex items-center space-x-2 xl:space-x-3">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-1.5 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            
            {isAuthenticated ? (
              <>
                {/* User Stats - More Compact */}
                <div className="flex items-center space-x-2 xl:space-x-3">
                  {/* XP Bar */}
                  <div className="flex items-center space-x-1" key={`xp-${renderKey}`}>
                    <Zap className="w-3.5 h-3.5 text-yellow-500" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 hidden xl:inline">{user?.xp || 0} XP</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 xl:hidden">{user?.xp || 0}</span>
                  </div>
                  
                  {/* Crowns */}
                  <div className="flex items-center space-x-1" key={`crowns-${userDataKey}`}>
                    <Crown className="w-3.5 h-3.5 crown-icon" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 hidden xl:inline">Crown</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{user?.crowns || 0}</span>
                  </div>
                  
                  {/* Trophies */}
                  <div className="flex items-center space-x-1" key={`trophies-${userDataKey}`}>
                    <Trophy className="w-3.5 h-3.5 trophy-icon" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 hidden xl:inline">Trophy</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{user?.trophies || 0}</span>
                  </div>
                  
                  {/* Streak */}
                  <div className="bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded-full text-xs font-medium hidden xl:block flex items-center">
                    <span className="text-[10px] leading-none">🔥</span>
                    <span className="ml-0.5 hidden xl:inline">Streak</span>
                    <span className="ml-0.5">{user?.streak || 0}</span>
                  </div>
                  <div className="bg-orange-100 text-orange-800 px-1 py-0.5 rounded-full text-xs font-medium xl:hidden flex items-center">
                    <span className="text-[10px] leading-none">🔥</span>
                    <span className="ml-0.5">{user?.streak || 0}</span>
                  </div>
                </div>

                {/* Profile Dropdown */}
                <div className="relative" ref={profileDropdownRef}>
                  <button
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    className="flex items-center space-x-1.5 text-sm rounded-lg text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1.5 transition-colors min-w-0"
                  >
                    <div className="w-7 h-7 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                      {user?.profileImage ? (
                        <img 
                          src={user.profileImage} 
                          alt="Profile" 
                          className="w-7 h-7 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-xs font-medium">
                          {getUserInitials()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 hidden lg:block">
                      <span className="font-medium max-w-[70px] truncate block text-xs" title={user?.username || 'User'}>
                        {user?.username || 'User'}
                      </span>
                    </div>
                    <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                  </button>

                  {isProfileDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                    >
                      {/* User Info Header */}
                      <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                        <div className="flex items-center space-x-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                            {user?.profileImage ? (
                              <img 
                                src={user.profileImage} 
                                alt="Profile" 
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            ) : (
                              <span className="text-white text-xs font-medium">
                                {getUserInitials()}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={user?.username || 'User'}>
                              {user?.username || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Level {user?.level || 1}</p>
                          </div>
                        </div>
                      </div>
                      
                      <Link
                        to="/profile"
                        onClick={() => setIsProfileDropdownOpen(false)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        <span>Profile</span>
                      </Link>
                      <Link
                        to="/profile/edit"
                        onClick={() => setIsProfileDropdownOpen(false)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </Link>
                      <hr className="my-1" />
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign out</span>
                      </button>
                    </motion.div>
                  )}
                </div>
              </>
            ) : (
              /* Auth Buttons */
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 px-2 py-1.5 rounded-lg text-sm font-medium transition-colors"
                >
                  <div className="flex items-center space-x-1">
                    <LogIn className="w-4 h-4" />
                    <span className="hidden xl:inline">Sign in</span>
                    <span className="xl:hidden">Login</span>
                  </div>
                </Link>
                <Link
                  to="/register"
                  className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:from-primary-700 hover:to-secondary-700 transition-all duration-200"
                >
                  <div className="flex items-center space-x-1">
                    <UserPlus className="w-4 h-4" />
                    <span className="hidden xl:inline">Sign up</span>
                    <span className="xl:hidden">Sign up</span>
                  </div>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
                      <button
            onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:text-gray-900 dark:focus:text-white p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 max-h-[calc(100vh-3.5rem)] overflow-y-auto"
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-base font-medium ${
                    isActive(item.href)
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            
            {/* Mobile Auth Section */}
            {isAuthenticated ? (
              <>
                <hr className="my-2" />
                <div className="px-3 py-2">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                      {user?.profileImage ? (
                        <img 
                          src={user.profileImage} 
                          alt="Profile" 
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-white text-sm font-medium">
                          {getUserInitials()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={user?.username || 'User'}>
                        {user?.username || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Level {user?.level || 1}</p>
                    </div>
                  </div>
                  
                  {/* Mobile User Stats */}
                  <div className="flex items-center justify-between text-sm mb-3">
                    <div className="flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-yellow-500" />
                      <span className="text-gray-700 dark:text-gray-300">{user?.xp || 0} XP</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Crown className="w-4 h-4 crown-icon" />
                      <span className="text-gray-700 dark:text-gray-300">Crown {user?.crowns || 0}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Trophy className="w-4 h-4 trophy-icon" />
                      <span className="text-gray-700 dark:text-gray-300">Trophy {user?.trophies || 0}</span>
                    </div>
                  </div>
                  <div className="text-center mb-3">
                    <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium flex items-center justify-center">
                      <span className="text-[10px] leading-none">🔥</span>
                      <span className="ml-1">{user?.streak || 0} day streak</span>
                    </span>
                  </div>
                  
                  <div className="space-y-1">
                    <Link
                      to="/profile"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <Link
                      to="/profile/edit"
                      onClick={() => setIsOpen(false)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsOpen(false);
                      }}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign out</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <hr className="my-2" />
                <div className="px-3 py-2 space-y-2">
                  <Link
                    to="/login"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <LogIn className="w-4 h-4" />
                    <span>Sign in</span>
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gradient-to-r from-primary-600 to-secondary-600 text-white"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Sign up</span>
                  </Link>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </nav>
  );
};

export default Navbar; 